import type { ReactNode } from 'react'

export default function Privacy() {
  return (
    <div className="container" style={{ maxWidth: 740, padding: '64px 24px 96px' }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.1em', marginBottom: 16 }}>
        LEGAL · PRIVACY POLICY
      </div>
      <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 8 }}>
        Privacy Policy
      </h1>
      <p className="muted" style={{ fontSize: 13, marginBottom: 48 }}>
        Last updated: 6 May 2026 · Effective immediately
      </p>

      <Prose>
        <p>
          Instadash is operated by Codextech Pty Ltd ("we", "us", "our"), a company registered in Victoria, Australia.
          This policy explains what data we collect, how we use it, and your rights.
        </p>

        <Section title="1. Data we collect">
          <SubSection title="Account data">
            When you sign in via GitHub OAuth or magic link, we receive your email address and, where provided by
            GitHub, your name and profile image. We store this to identify your account and communicate with you.
          </SubSection>
          <SubSection title="Grid data">
            Any JSON, CSV, NDJSON, or log files you push to Instadash are stored on your behalf in Cloudflare R2
            object storage. You own this data. We process it only to provide the service (schema inference, API
            endpoints, version history).
          </SubSection>
          <SubSection title="Usage data">
            We log API requests, push events, and page views to operate and improve the service. Logs include
            timestamps, endpoint paths, response codes, and anonymised IP addresses. We do not build advertising
            profiles from this data.
          </SubSection>
          <SubSection title="Billing data">
            Paid plan payments are handled by Stripe. We never see or store your full card number. We receive a
            Stripe customer ID and subscription status so we can provision your plan.
          </SubSection>
          <SubSection title="Cookies and local storage">
            We use a single session cookie issued by our authentication system (better-auth) to keep you signed in.
            No third-party tracking cookies. No analytics pixels.
          </SubSection>
        </Section>

        <Section title="2. How we use your data">
          <ul>
            <li>Authenticate you and maintain your session</li>
            <li>Store, serve, and version your grid data</li>
            <li>Process payments and manage your subscription</li>
            <li>Send transactional emails (magic-link sign-in, billing receipts)</li>
            <li>Debug incidents and investigate abuse</li>
            <li>Improve the product using aggregated, anonymised usage signals</li>
          </ul>
          <p>We do not sell your data. We do not use your data for advertising.</p>
        </Section>

        <Section title="3. Data storage and transfers">
          <p>
            Your data is stored on Cloudflare's global infrastructure (R2, D1, Workers KV). Cloudflare's primary
            processing regions include the United States and European Union. By using Instadash you consent to your
            data being processed in these regions.
          </p>
          <p>
            Platform-tier customers can bring their own Cloudflare account and R2 bucket to keep data within a
            specific region.
          </p>
        </Section>

        <Section title="4. Third parties">
          <p>We share data with the following third parties only as necessary to provide the service:</p>
          <ul>
            <li><strong>Cloudflare</strong> — infrastructure, storage, CDN</li>
            <li><strong>Stripe</strong> — payment processing</li>
            <li><strong>GitHub</strong> — OAuth sign-in (only if you choose GitHub login)</li>
          </ul>
          <p>
            We do not share your data with any other third parties without your explicit consent, except where
            required by law.
          </p>
        </Section>

        <Section title="5. Data retention">
          <p>
            Grid data is retained for as long as your account is active. On account deletion, your grids and
            associated data are deleted within 30 days. Version history snapshots are deleted on the same schedule.
          </p>
          <p>
            Billing records are retained for 7 years as required by Australian tax law.
          </p>
          <p>
            Server logs are retained for 90 days and then permanently deleted.
          </p>
        </Section>

        <Section title="6. Your rights">
          <p>
            Under the Australian Privacy Act 1988 and, where applicable, the EU General Data Protection Regulation
            (GDPR), you have the right to:
          </p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your account and associated data</li>
            <li>Export your grid data at any time via the CLI (<code>instadash export</code>)</li>
            <li>Object to or restrict certain processing</li>
            <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) or your local
              data protection authority</li>
          </ul>
          <p>
            To exercise any of these rights, email us at{' '}
            <a href="mailto:privacy@instadash.io">privacy@instadash.io</a>. We will respond within 30 days.
          </p>
        </Section>

        <Section title="7. Security">
          <p>
            All data in transit is encrypted via TLS. Grid data at rest is encrypted by Cloudflare R2. Session
            tokens are stored in httpOnly, SameSite cookies. API keys are stored as hashed values — we cannot
            recover a key once issued.
          </p>
          <p>
            If you discover a security vulnerability, please report it to{' '}
            <a href="mailto:security@instadash.io">security@instadash.io</a> before public disclosure.
          </p>
        </Section>

        <Section title="8. Children">
          <p>
            Instadash is not directed at children under 16. We do not knowingly collect personal data from children.
            If you believe a child has provided us with personal data, contact us at{' '}
            <a href="mailto:privacy@instadash.io">privacy@instadash.io</a> and we will delete it promptly.
          </p>
        </Section>

        <Section title="9. Changes to this policy">
          <p>
            We may update this policy from time to time. We will notify you by email at least 14 days before material
            changes take effect. Continued use of the service after the effective date constitutes acceptance of the
            updated policy.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            Questions or requests about this policy:
          </p>
          <p>
            <strong>Codextech Pty Ltd</strong><br />
            Melbourne, Victoria, Australia<br />
            <a href="mailto:privacy@instadash.io">privacy@instadash.io</a>
          </p>
        </Section>
      </Prose>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 40 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--fg)' }}>{title}</h2>
      {children}
    </section>
  )
}

function SubSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)', marginBottom: 6 }}>{title}</h3>
      <p>{children}</p>
    </div>
  )
}

function Prose({ children }: { children: ReactNode }) {
  return (
    <div style={{
      fontSize: 14,
      lineHeight: 1.75,
      color: 'var(--fg-2)',
    }}
    className="prose-content"
    >
      {children}
    </div>
  )
}
